import { useEffect, useState } from "react";
import { Button, Card, Empty, Skeleton, Tag, Typography } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { streamBacklogThemes } from "../../lib/api";

// Shape mirrors the backend payload from `_label_clusters` in
// functions/domains/exec_dashboard.py — each theme carries the AI-generated
// cluster name under `theme`, a total `count`, the intake/project split, and
// up to a handful of representative `items`. (Earlier this interface read
// `label`/`description`/`examples`, none of which the backend sends, so every
// row rendered an empty name and only the count Tag.)
interface ThemeItem {
  theme: string;
  count: number;
  intakeCount?: number;
  projectCount?: number;
  items?: { id: string; kind: string; label: string }[];
}

interface ThemesMeta {
  themes?: ThemeItem[];
  updatedAt?: string;
  // Backend sends `source: "cache" | "fresh"`, not a `cached` boolean.
  source?: string;
}

function formatRelative(iso: string | undefined): string {
  if (!iso) return "";
  try {
    const ts = Date.parse(iso);
    if (Number.isNaN(ts)) return "";
    const diff = Date.now() - ts;
    const minutes = Math.round(diff / 60000);
    if (minutes < 1) return "just now";
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.round(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.round(hours / 24);
    return `${days}d ago`;
  } catch {
    return "";
  }
}

export default function BacklogThemesCard() {
  const [themes, setThemes] = useState<ThemeItem[] | null>(null);
  const [updatedAt, setUpdatedAt] = useState<string | undefined>(undefined);
  const [cached, setCached] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchThemes = async (force = false) => {
    setLoading(true);
    setError(null);
    try {
      await streamBacklogThemes(
        {
          onMeta: (meta) => {
            const m = meta as ThemesMeta;
            if (Array.isArray(m.themes)) setThemes(m.themes);
            if (m.updatedAt) setUpdatedAt(m.updatedAt);
            setCached(m.source === "cache");
          },
        },
        { force }
      );
    } catch (err) {
      console.error("Failed to load backlog themes:", err);
      setError("Could not load themes right now.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchThemes(false);
    // intentionally no deps — fired once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Card
      title="In-flight and backlog themes"
      extra={
        <Button
          type="text"
          size="small"
          icon={<ReloadOutlined spin={loading} />}
          onClick={() => fetchThemes(true)}
          disabled={loading}
          aria-label="Refresh themes"
        >
          Refresh
        </Button>
      }
    >
      {loading && !themes ? (
        <Skeleton active paragraph={{ rows: 4 }} />
      ) : error ? (
        <Typography.Text type="danger">{error}</Typography.Text>
      ) : themes && themes.length > 0 ? (
        <div>
          <ul style={{ paddingLeft: 18, marginBottom: 12 }}>
            {themes.map((theme, idx) => {
              const split = [
                theme.intakeCount
                  ? `${theme.intakeCount} intake${theme.intakeCount === 1 ? "" : "s"}`
                  : null,
                theme.projectCount
                  ? `${theme.projectCount} project${theme.projectCount === 1 ? "" : "s"}`
                  : null,
              ]
                .filter(Boolean)
                .join(" · ");
              return (
                <li key={`${theme.theme}-${idx}`} style={{ marginBottom: 12 }}>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                    <Typography.Text strong style={{ color: "#092940" }}>
                      {theme.theme}
                    </Typography.Text>
                    <Tag color="blue" style={{ marginInlineStart: 0 }}>
                      {theme.count} item{theme.count === 1 ? "" : "s"}
                    </Tag>
                  </div>
                  {split && (
                    <Typography.Paragraph
                      type="secondary"
                      style={{ margin: "2px 0 4px 0", fontSize: 13 }}
                    >
                      {split}
                    </Typography.Paragraph>
                  )}
                  {theme.items && theme.items.length > 0 && (
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      e.g.&nbsp;
                      {theme.items
                        .slice(0, 3)
                        .map((it) => it.label)
                        .join(" · ")}
                    </Typography.Text>
                  )}
                </li>
              );
            })}
          </ul>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {cached ? "Cached" : "Fresh"}{updatedAt ? ` · ${formatRelative(updatedAt)}` : ""}
          </Typography.Text>
        </div>
      ) : (
        <Empty description="Not enough intakes or projects with embeddings to cluster yet" />
      )}
    </Card>
  );
}
